"use strict";

import DomHelper from '../Helpers/Dom';
import Utils from '../Helpers/Utils';

export default class ElementBinding {

	constructor(settings = {}, debugMode = false)
	{
		let self = this;
		self.elements = {};
		self.settings = settings;
		self.isInDebugMode = debugMode;
	}

	/**
	 * Add event type that should trigger data refresh
	 * @param {String} eventType
	 * @param {Boolean} hardRefresh
	 * @param {Function} callback
	 * @param {Boolean} useCapture
	 * @return
	 */
	addRefreshEvent(eventType, hardRefresh = false, callback = null, useCapture = false)
	{
		let self = this;
		//self.debug("ElementBinding.addRefreshEvent called with eventtype '%s'", eventType);
		window.addEventListener(eventType, (event) => {
			if (event.target && (event.target.classList.contains('sparxpres-select') || event.target.classList.contains('sparxpres-slider'))) {
				return;
			}
			self.debug("ElementBinding, change event caught");

			if (hardRefresh) {
				self.hardRefresh(callback);
			} else {
				self.refresh(callback);
			}
		}, useCapture);
	}

    /**
	 * Add any event to an element
     * @param alias
     * @param eventType
     * @param callback
     * @returns {boolean}
     */
	addEventListenerToElement(alias, eventType, callback)
	{
		let self = this;
		let element = self.elements[alias];

		if (element) {
			element.element.addEventListener(eventType, callback);

			return true;
		}

		return false;
	}

	/**
     * Add elements to data handler
     * key = alias
     * value = identifier
     * @param {Object} element
     * @return
     */
	addElements(elements)
	{
		let self = this;
		for (let key in elements) {
			self.addElement(key, elements[key]);
		}
	}

	/**
     * Add element to watch list
     * @param {String} alias
     * @param {String} identifier
     * @return
     */
	addElement(alias, identifier, cleanHiddenField = false, getValueFromAttribute = "")
	{
		//this.debug("ElementBinding.addElement, alias is '%s', identifier is '%s'", alias, identifier);
		try {
			let _identifier = identifier ? identifier : "";

			if (typeof _identifier === "string") {
				this._addElement(alias, _identifier, cleanHiddenField, getValueFromAttribute);
				this.elements[alias].identifier = _identifier;
				return;
			}

			if (Array.isArray(_identifier)) {
				for (const element of _identifier) {
					this._addElement(alias, element, cleanHiddenField, getValueFromAttribute);
					if (this.elements[alias].element && this.elements[alias].value) {
						this.elements[alias].identifier = _identifier;	// set the array as identifier
						return;
					}
				}

				if (_identifier.length === 0) {
					this._addElement(alias, "", cleanHiddenField, getValueFromAttribute);
				}

				this.elements[alias].identifier = _identifier;
			}

		} catch (e) {
			console.log("So what elements fail?", alias, identifier, this.elements[alias]);
			console.log(e);
		}
	}

	/**
	 * Add element to watch list
	 * @param {String} alias
	 * @param {String} identifier
	 * @param cleanHiddenField
	 * @param getValueFromAttribute
	 * @return
	 */
	_addElement(alias, identifier, cleanHiddenField = false, getValueFromAttribute = "")
	{
		let skipElementIfNoValue = alias === 'price'
			&& this.settings.general.hasOwnProperty('skipElementBindIfNoValue')
			&& this.settings.general.skipElementBindIfNoValue === "1";

		let element = identifier ? DomHelper.getElement(identifier) : null;

		let value = '';
        let oldValue = '';
        let replaceOldValue = true;

		let isHidden = function(el) {
		    return (el.offsetParent === null);
		};

		if (!this.elements[alias]) {
			this.elements[alias] = {
				"alias": alias
			};
		}

		if (!this.elements[alias].hasOwnProperty("attributeValue")) {
			this.elements[alias]['attributeValue'] = getValueFromAttribute;
		}

		try {
			if (element && cleanHiddenField && isHidden(element)) {
				value = '';
				element = null;
			} else {
				value = this._getElementValue(this.elements[alias]);
			}
		} catch(e) {}

		if (element || !this.elements[alias].element) {
			if (skipElementIfNoValue) {
				value = this._getElementValue({
					"element": element,
					"alias": alias,
					"attributeValue": getValueFromAttribute
				});

				if (value && value !== '' && value !== null) {
                    this.elements[alias]['element'] = element;
                    replaceOldValue = false;
				}
			} else {
                this.elements[alias]['element'] = element;

                if (this.elements[alias].element) {
                    value = this._getElementValue(this.elements[alias]);
                    replaceOldValue = false;
                }
			}
		} else {
			this.elements[alias]['element'] = null;
		}

        if (value || value === "" || !this.elements[alias].value) {
			if (this.elements[alias].value) { // && replaceOldValue) {
				oldValue = this.elements[alias].value;
			}

			this.elements[alias]['value'] = value;

			if (oldValue !== value) {
				let dirtyElement = {};
				dirtyElement[alias] = {
					"oldValue": oldValue,
					"newValue": value
				}

				Utils.fireSparxpresEvent("sparxpresDirtyElements", dirtyElement);
			}

        }

	}

	/**
	 * Add content to, before or after element
	 * @param {String} alias
	 * @param {String} insertPosition
	 * @param {String} wrapperClass
	 * @param {String} wrapperClassStyle
	 * @param {String} content
	 * @param {String} style
	 */
	addToElement(alias, insertPosition, wrapperClass = '', wrapperClassStyle = '', content = '', style = '') {
		if (!this.elements[alias]) {
			throw new Error("Element not added to DataBinding");
		}

		if (!this.elements[alias].element) {
			throw new Error("Element has been added to DataBinding, but not found in the DOM");
		}

		let element = this.elements[alias].element;

		switch (insertPosition) {
			case "append":
				DomHelper.insertAppend(element, wrapperClass, wrapperClassStyle, content, style);
				break;
			case "after":
				DomHelper.insertAfter(element, wrapperClass, wrapperClassStyle, content, style);
				break;
			case "before":
				DomHelper.insertBefore(element, wrapperClass, wrapperClassStyle, content, style);
				break;
			case "prepend":
				DomHelper.insertPrepend(element, wrapperClass, wrapperClassStyle, content, style);
				break;
		}
	}

	/**
	 * Get value from dom
	 * @param {String} alias
	 * return {String}
	 */
	getValue(alias)
	{
		if (!this.elements[alias]) {
			return null;
		}

		return this.elements[alias].value;
	}

	/**
	 * Get value from dom
	 * @param {String} alias
	 * return {String}
	 */
	exists(alias)
	{
		return !(!this.elements[alias]);
	}

	/**
	 * Get element from dom
	 * @param {String} alias
	 * return {String}
	 */
	getElement(alias)
	{
		if (!this.elements[alias]) {
			return null;
		}

		return this.elements[alias].element;
	}

	/**
	 * Get element from dom
	 * @param {String} alias
	 * @param attribute
	 * return {String}
	 */
	getAttribute(alias, attribute)
	{
		if (!this.elements[alias] || !this.elements[alias].element) {
			return null;
		}

		return this.elements[alias].element.getAttribute(attribute);
	}

	/**
	 * Set value in dom
	 * @param {String} alias
	 * @param {String} value
	 * return {String}
	 */
	setValue(alias, value)
	{
		if (!this.elements[alias]) {
			throw new Error("Element not found");
		}

		this.elements[alias].value = value;
		this._setElementValue(this.elements[alias].element, value);

		return this;
	}

	/**
	 * Set values in dom
	 * @param {Object} aliasesValues
	 * return {String}
	 */
	setValues(aliasesValues)
	{
		let self = this;

		for (let key in aliasesValues) {
			self.setValue(key, aliasesValues[key]);
		}

		return self;
	}

	/**
	 * Refresh values from cached elements
	 * @return
	 */
	refresh(callback = null) 
	{
		let self = this;
		setTimeout(() => {
			let dirtyElements = {};

			for (let key in self.elements) {
				let oldValue = self.elements[key].value;
				if (!self.elements[key].element) {
					// Run addElement again since element wasn't found on first run
					self.addElement(key, self.elements[key].identifier);
				} else {
					self.elements[key].value = self._getElementValue(self.elements[key]);
				}
				let newValue = self.elements[key].value;
				if (oldValue !== newValue) {
					dirtyElements[alias] = {
						"oldValue": oldValue,
						"newValue": newValue
					}
				}
			}

			if (callback) {
				callback(dirtyElements);
			}
		}, 0);
	}

	/**
	 * Fresh run
	 * @return
	 */
	hardRefresh(callback = null)
	{
		let self = this;
		setTimeout(() => {
			let dirtyElements = {};

			for (let alias in self.elements) {
				let identifier = self.elements[alias].identifier;
				let oldValue = self.elements[alias].value;
				self.addElement(alias, identifier);
				let newValue = self.elements[alias].value;

				if (oldValue !== newValue) {
					dirtyElements[alias] = {
						"oldValue": oldValue,
						"newValue": newValue
					}
				}
			}

			/**
			for (let key in this.elements) {
				console.log("In addElement: ", key, ", " + this.elements[key].value);
			}*/

			if (callback) {
				callback(dirtyElements);
			}
		}, 0);
	}

	/**
	 * Show element
	 * @param  {String} alias
	 * @param  {String} displayType
	 * @return
	 */
	show(alias, displayType = 'block') {
		if (!this.elements[alias]) {
			return;
			//throw new Error("Element not added to DataBinding");
		}

		if (alias === "checkoutSubmitButton") {
			if (this.elements[alias]['identifier'].length > 1) {
				this.elements[alias]['identifier'].forEach(function(item, index) {
					DomHelper.show(item, displayType);
				});
				return;
			}
		}

		DomHelper.show(this.elements[alias].element, displayType);
	}

	/**
	 * Hide element
	 * @param {String} alias
	 * @param {String} displayType
	 * @return
	 */
	hide(alias, displayType = 'none') {
		if (!this.elements[alias]) {
			return;
			//throw new Error("Element not added to DataBinding");
		}

		if (alias === "checkoutSubmitButton") {
			if (this.elements[alias]['identifier'].length > 1) {
				this.elements[alias]['identifier'].forEach(function(item, index) {
					DomHelper.hide(item, displayType);
				});
				return;
			}
		}

		DomHelper.hide(this.elements[alias].element, displayType);
	}

	/**
	 * Enable element
	 * @param  {String} alias
	 * @return
	 */
	enable(alias) {
		if (!this.elements[alias]) {
			throw new Error("Element not added to DataBinding");
		}

		DomHelper.enable(this.elements[alias].element);
	}

	/**
	 * Disable element
	 * @param  {String} alias
	 * @return
	 */
	disable(alias) {
		if (!this.elements[alias]) {
			throw new Error("Element not added to DataBinding");
		}
			
		DomHelper.disable(this.elements[alias].element);
	}

	/**
	 * Remove from dom
	 */
	removeFromDom(alias)
	{
		let element = this.elements[alias];
		if (element && element.element) {
			element.element.remove();
			this.elements[alias].element = null;
		}
	}

	/**
	 * Fresh run for a specific element
	 * @param alias
	 * @return
	 */
	refreshElement(alias)
	{
		let oldValue = this.elements[alias].value;
		this.elements[alias].value = this._getElementValue(this.elements[alias]) || oldValue;

		return this.elements[alias].value !== oldValue;
	}

	/**
	 * Fresh run for a specific element
	 * @param {String} alias
	 * @param {Boolean} forceValueReset
	 * @param {Boolean} cleanHiddenField
	 * @return
	 */
	hardRefreshElement(alias, forceValueReset = false, cleanHiddenField = false)
	{
		if (!this.elements[alias]) {
			return false;
		}

		let oldValue = this.elements[alias].value;

		if (forceValueReset) {
			this.elements[alias].element = null;
			this.elements[alias].value = "";
		}

		this.addElement(alias, this.elements[alias].identifier, cleanHiddenField);
		let newValue = this.elements[alias].value;

		return (newValue && newValue !== oldValue);
	}

    /**
     * Fresh run for a specific element
     * @param {String} alias
     * @param {Object} identifier
     * @return
     */
    rebindElement(alias, identifier)
    {
        let oldValue = this.elements[alias].value;

		this.elements[alias].element = null;
		this.elements[alias].value = "";
		this.elements[alias].identifier = "";

        this.addElement(alias, identifier);
        let newValue = this.elements[alias].value;

        return (newValue && newValue !== oldValue);
    }

	/**
	 * Get element value
	 * @param  {Object} element
	 * @return {String}
	 */
	_getElementValue(element) {
		let _element = element.element;

		if (!_element) {
			//throw new Error("No element found");
			return "";
		}

		// if there is set a attribute value in the config, then use it
		if (element.attributeValue) {
			return this.getAttribute(element.alias, element.attributeValue);
		}

		//return _element.value || _element.innerHTML;
		//return _element.value || (_element.textContent !== null ? _element.textContent.trim() : _element.textContent);
		if ((_element.tagName === "INPUT" || _element.tagName === "SELECT") && (_element.value || _element.value === "")) {
			return _element.value;
		} else {
			let _child = _element.firstChild;
			let _texts = [];
			while (_child) {
				if (_child.nodeType === Node.TEXT_NODE) {
					_texts.push(_child.data);
				}
				_child = _child.nextSibling;
			}
			return _texts.join("").trim();
		}
	}

	/**
	 * Update value in dom
	 * @param  {Object} element
	 * @param  {String} value
	 * @return
	 */
	_setElementValue(element, value) {
		if (!element) {
			throw new Error("No element found");			
		}

		if (element.value) {
			element.value = value;
		} else if (element.innerHTML) {
			element.innerHTML = value;
		}
	}

	/**
	 * Log a message if in debug mode
	 * @param msg
	 * @param args
	 */
	debug(msg, ...args) {
		Utils.debug(this.isInDebugMode, msg, ...args);
	}

}